// DataSources — the reference files forms look values up from (branches, assets,
// suppliers, sites). Listed via POST datasource/all with server-side pagination,
// search and single-column sort; normalization stays isolated in toDataSource so
// any backend drift is a one-file fix.
//
// `orderBy.key` values are a confirmed enum whose members double as the sortable
// columns' keys, so the column a user clicks and the value the body carries
// cannot drift apart — the SubmissionSortKey idiom, and the reason there is no
// UI-key → backend-key map anywhere.
//
// `dataSourceType` arrives as a READY-TO-DISPLAY string ("Contact list", "Geo
// dataset", …), not a slug, so the type chip renders it literally and no i18n key
// exists for it — backend data values stay literal.
//
// Delete is a TRUE bulk endpoint (`{ dataSourceIds: string[] }`), so the row
// kebab and the multi-select bar both go through one call — unlike inspections,
// which has to fire single deletes in parallel for lack of one.
//
// Update is PATCH datasource/:id taking the CREATE body unchanged, so one modal
// serves Add new and Edit — the DataSourceEntryModal idiom.
//
// A source's COLUMNS have their own three routes: add, rename
// (PATCH :id/columns/:columnId, `{ columnName }` only) and a bulk delete
// (`{ columnIds: string[] }`). There is deliberately no retype — the rename
// payload carries no `dataType`, so a column's type is fixed once it exists.
//
// A source can also be created wholesale FROM a CSV — see importDataSource and
// parseDataSourceCsv. The bytes go to the bucket first (media/upload-public-file
// reserves the URL), so the import call itself stays ordinary JSON.
//
// Export is per-source and exports that source's ENTRIES: POST
// export-manager/export-datasource takes `{ dataSourceId, entryIds? }`, and
// OMITTING entryIds is what asks for every row (see ExportDataSourcePayload).
// It queues a JOB rather than returning a file — the response says the export
// started, not where it landed, so callers toast and stop; the finished file
// reaches the user through the notifications bell, which resolves the exportId
// via export-manager/:id/download-url.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { FormDateFilter, FormPerson } from './formsApi';

/** Backend `orderBy.key` values — the confirmed set, mirrored whole so the
 *  contract is readable here even where the UI has no column for a member
 *  (`createdAt`/`updatedAt` are accepted but nothing displays them). Member
 *  names are PascalCase per this repo's other sort enums; the VALUES are the
 *  wire strings. */
export enum DataSourceSortKey {
  SchemaName = 'schemaName',
  DataSourceType = 'dataSourceType',
  ExternalId = 'externalId',
  TotalRows = 'totalRows',
  LastUpdatedDate = 'lastUpdatedDate',
  CreatedAt = 'createdAt',
  UpdatedAt = 'updatedAt',
}

export interface DataSourceListItem {
  id: string;
  schemaName: string;
  /** Display string, not a slug — chipped as-is. */
  dataSourceType: string;
  externalId: string;
  totalRows: number;
  /** Null when the source has no recorded updater. */
  lastUpdatedBy: FormPerson | null;
  /** ISO. */
  lastUpdatedDate: string;
}

export interface DataSourcePage {
  items: DataSourceListItem[];
  total: number;
}

/** A column definition within a data source's schema. */
export interface DataSourceColumn {
  columnId: string;
  columnName: string;
  dataType: string;
}

/** A single data source with its column schema (from the details endpoint). */
export interface DataSourceDetail {
  id: string;
  schemaName: string;
  dataSourceType: string;
  externalId: string;
  totalRows: number;
  columns: DataSourceColumn[];
}

/** POST datasource/all body — `page`/`limit` required; `search` and `orderBy`
 *  are omitted (undefined) when unset, which JSON.stringify drops. */
export interface DataSourceQueryArgs {
  search?: string;
  orderBy?: { key: DataSourceSortKey; type: 'asc' | 'desc' };
  page: number;
  limit: number;
  // Toolbar filter selections — omitted when empty. `lastUpdated` reuses the
  // shared date-preset contract (FormDateFilter; "Any time" = send nothing).
  lastUpdatedBy?: string[];
  lastUpdated?: FormDateFilter;
  /** yyyy-mm-dd; sent only with CUSTOM. */
  lastUpdatedStart?: string;
  lastUpdatedEnd?: string;
}

/** One picker row from GET datasource/selection. Confirmed shape — every field
 *  is always present, so no optionals and no normalizer fallbacks. */
export interface DataSourceSelectionItem {
  id: string;
  schemaName: string;
  columns: DataSourceColumn[];
}

export interface CreateDataSourcePayload {
  schemaName: string;
  externalId: string;
  dataSourceType: string;
}

/** PATCH datasource/:id — the body is the create payload unchanged, so the one
 *  form serves both modes and only the id rides outside it (the
 *  data-source-entries idiom). */
export interface UpdateDataSourceArgs {
  id: string;
  body: CreateDataSourcePayload;
}

/** POST import/import-datasource — creates a source FROM an already-uploaded CSV.
 *  The three text fields are the create payload's; `fileName`/`filePathUrl`
 *  point at the bucket object, so the request stays ordinary JSON and the bytes
 *  never touch the (encrypting) api client. The CSV's own first two rows supply
 *  the column names and their dataTypes, which is why none are sent here. */
export interface ImportDataSourcePayload {
  schemaName: string;
  dataSourceType: string;
  externalId: string;
  fileName: string;
  filePathUrl: string;
}

/** The SAME route as ImportDataSourcePayload, with the other body: this one
 *  replaces the records inside an existing source rather than creating one, so
 *  it names the target by id and carries none of the schema fields. The CSV's
 *  own header must already match that source's columns (validateEntriesCsv). */
export interface ImportDataSourceEntriesPayload {
  dataSourceId: string;
  fileName: string;
  filePathUrl: string;
}

/** POST export-manager/export-datasource — queues an export of one source's
 *  entries. `entryIds` is OMITTED ENTIRELY to export everything; its absence is
 *  the signal, which is why the caller builds this body conditionally rather
 *  than passing an undefined key. */
export interface ExportDataSourcePayload {
  dataSourceId: string;
  entryIds?: string[];
}

/** POST datasource/delete-multiple body — one call for any number of sources. */
export interface DeleteDataSourcesPayload {
  dataSourceIds: string[];
}

export interface AddDataSourceColumnPayload {
  id: string;
  columnName: string;
  dataType: string;
}

/** PATCH datasource/:id/columns/:columnId — a RENAME. The payload is
 *  `{ columnName }` and carries no `dataType`, so a column's type is fixed once
 *  it exists; the edit form renders the type picker locked rather than offering
 *  a change the contract cannot express. */
export interface UpdateDataSourceColumnArgs {
  /** The data source the column belongs to. */
  id: string;
  columnId: string;
  columnName: string;
}

/** POST datasource/:id/columns/delete-multiple — bulk, scoped to one source, so
 *  the bar's single and multi delete are the same call. Note the key is
 *  `columnIds` — a THIRD spelling alongside `dataSourceIds` (datasource) and
 *  `entryIds` (data-source-entries). Don't "harmonise" them. */
export interface DeleteDataSourceColumnsArgs {
  id: string;
  columnIds: string[];
}

// ---- raw shapes (per the confirmed responses) ----
interface ApiDataSourceSelection {
  _id: string;
  schemaName: string;
  dataSourceColumnSchema: DataSourceColumn[];
}
interface ApiDataSourcePerson {
  _id: string;
  user: { _id: string; firstName: string; lastName: string };
}
interface ApiDataSource {
  _id: string;
  schemaName: string;
  dataSourceType: string;
  externalId: string;
  totalRows: number;
  dataSourceColumnSchema?: DataSourceColumn[];
  /** Absent on a source nothing has updated yet. */
  lastUpdatedBy?: ApiDataSourcePerson | null;
  lastUpdatedDate: string;
}
interface ApiDataSourcePage {
  items: ApiDataSource[];
  /** Note the name: `totalRecords`, as data-source-entries uses — NOT `total`. */
  totalRecords: number;
}

// Same nested `{ user: { firstName, lastName } }` shape as the submissions and
// forms payloads — mirrors their mapper into the shared FormPerson rather than
// declaring a third person type. A blank name is null, so the cell dashes
// instead of rendering a lone space.
const toPerson = (p?: ApiDataSourcePerson | null): FormPerson | null => {
  if (!p) return null;
  const fullName = `${p.user.firstName} ${p.user.lastName}`.trim();
  return fullName ? { id: p._id, fullName } : null;
};

const toDataSource = (p: ApiDataSource): DataSourceListItem => ({
  id: p._id,
  schemaName: p.schemaName,
  dataSourceType: p.dataSourceType,
  externalId: p.externalId,
  totalRows: p.totalRows,
  lastUpdatedBy: toPerson(p.lastUpdatedBy),
  lastUpdatedDate: p.lastUpdatedDate,
});

const toDataSourceDetail = (p: ApiDataSource): DataSourceDetail => ({
  id: p._id,
  schemaName: p.schemaName,
  dataSourceType: p.dataSourceType,
  externalId: p.externalId,
  totalRows: p.totalRows,
  columns: p.dataSourceColumnSchema ?? [],
});

export const dataSourceApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getDataSources: build.query<DataSourcePage, DataSourceQueryArgs>({
      query: (body) => ({ ...ROUTES.dataSource.list, data: body }),
      transformResponse: (res: ApiDataSourcePage) => ({
        items: res.items.map(toDataSource),
        total: res.totalRecords,
      }),
      providesTags: [{ type: 'DataSource', id: 'LIST' }],
    }),
    // Picker feed for the builder's dataSource field — carries each source's
    // column schema, so choosing one needs no follow-up details request.
    getDataSourceSelection: build.query<DataSourceSelectionItem[], void>({
      query: () => ({ ...ROUTES.dataSource.selection }),
      transformResponse: (res: ApiDataSourceSelection[]) =>
        res.map((d) => ({
          id: d._id,
          schemaName: d.schemaName,
          columns: d.dataSourceColumnSchema,
        })),
      providesTags: [{ type: 'DataSource', id: 'LIST' }],
    }),
    createDataSource: build.mutation<{ _id?: string }, CreateDataSourcePayload>({
      query: (body) => ({ ...ROUTES.dataSource.create, data: body }),
      // Must match what the list PROVIDES, or the new row never appears: the
      // modal lives inside the already-mounted page, so refetchOnMountOrArgChange
      // never fires for it.
      invalidatesTags: [{ type: 'DataSource', id: 'LIST' }],
    }),
    importDataSource: build.mutation<{ _id?: string }, ImportDataSourcePayload>({
      query: (body) => ({ ...ROUTES.imports.dataSource, data: body }),
      // Same reason as createDataSource: the modal lives inside the mounted
      // page, so refetchOnMountOrArgChange never fires and only this makes the
      // imported source appear.
      invalidatesTags: [{ type: 'DataSource', id: 'LIST' }],
    }),
    importDataSourceEntries: build.mutation<{ _id?: string }, ImportDataSourceEntriesPayload>({
      query: (body) => ({ ...ROUTES.imports.dataSource, data: body }),
      // The rows were replaced wholesale, and the details query behind the
      // entries header's record/column counts is now stale too.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DataSourceEntries', id: 'LIST' },
        { type: 'DataSource', id: arg.dataSourceId },
      ],
    }),
    // No invalidation on purpose: queueing an export changes nothing this app
    // renders. (peopleApi's startGuestExport invalidates its list — that has no
    // justification and is not copied here.)
    exportDataSource: build.mutation<unknown, ExportDataSourcePayload>({
      query: (body) => ({ ...ROUTES.exportManager.exportDataSource, data: body }),
    }),
    updateDataSource: build.mutation<{ _id?: string }, UpdateDataSourceArgs>({
      query: ({ id, body }) => ({ ...ROUTES.dataSource.update(id), data: body }),
      // Both tags, because an edit changes what TWO caches show: the listing
      // (name/type/external id columns) and this source's own detail, which the
      // entries page reads its title and meta line from.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DataSource', id: 'LIST' },
        { type: 'DataSource', id: arg.id },
      ],
    }),
    deleteDataSources: build.mutation<unknown, DeleteDataSourcesPayload>({
      query: (body) => ({ ...ROUTES.dataSource.deleteMultiple, data: body }),
      // LIST is what the listing provides — the whole module's invalidations
      // were mismatched before the rebuild, so this must not reintroduce that.
      invalidatesTags: [{ type: 'DataSource', id: 'LIST' }],
    }),
    getDataSourceDetails: build.query<DataSourceDetail, string>({
      query: (id) => ({ ...ROUTES.dataSource.details(id) }),
      transformResponse: (res: ApiDataSource) => toDataSourceDetail(res),
      // Per-id, so adding a column refreshes THAT source rather than every
      // cached detail.
      providesTags: (_result, _error, id) => [{ type: 'DataSource', id }],
    }),
    addDataSourceColumn: build.mutation<
      { dataSourceId?: string; columnId?: string },
      AddDataSourceColumnPayload
    >({
      query: ({ id, ...body }) => ({ ...ROUTES.dataSource.addColumn(id), data: body }),
      // Refreshes this source's columns — the entries table headers and the
      // create-entry form both read them from the details query.
      invalidatesTags: (_result, _error, arg) => [{ type: 'DataSource', id: arg.id }],
    }),
    updateDataSourceColumn: build.mutation<{ _id?: string }, UpdateDataSourceColumnArgs>({
      query: ({ id, columnId, columnName }) => ({
        ...ROUTES.dataSource.updateColumn(id, columnId),
        data: { columnName },
      }),
      // Same single tag as addDataSourceColumn, and for the same reason: the
      // details query is what feeds the table headers and the add-row form's
      // labels. Deliberately NOT the LIST — a rename changes none of the
      // listing's columns, and it refetches on mount anyway.
      invalidatesTags: (_result, _error, arg) => [{ type: 'DataSource', id: arg.id }],
    }),
    deleteDataSourceColumns: build.mutation<unknown, DeleteDataSourceColumnsArgs>({
      query: ({ id, columnIds }) => ({
        ...ROUTES.dataSource.deleteColumns(id),
        data: { columnIds },
      }),
      // TWO tags, unlike the add/rename pair. Dropping a column removes its
      // value from every entry server-side, so the cached ROWS are genuinely
      // stale, not merely unread — refreshing only the headers would leave real
      // data un-refetched.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DataSource', id: arg.id },
        { type: 'DataSourceEntries', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetDataSourcesQuery,
  useGetDataSourceSelectionQuery,
  useCreateDataSourceMutation,
  useImportDataSourceMutation,
  useImportDataSourceEntriesMutation,
  useExportDataSourceMutation,
  useUpdateDataSourceMutation,
  useDeleteDataSourcesMutation,
  useGetDataSourceDetailsQuery,
  useAddDataSourceColumnMutation,
  useUpdateDataSourceColumnMutation,
  useDeleteDataSourceColumnsMutation,
} = dataSourceApi;
